import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Landing from './pages/Landing';
import SplashScreen from './pages/SplashScreen';
import DashboardV2 from './pages/DashboardV2';
import MoodTimeline from './pages/MoodTimeline';
import AIChat from './pages/AIChat';
import VoiceChatPage from './pages/VoiceChatPage';
import CommunityEnhanced from './pages/CommunityEnhanced';
import Journal from './pages/Journal';
import MusicEnhanced from './pages/MusicEnhanced';
import History from './pages/History';
import Onboarding from './pages/Onboarding';
import Settings from './pages/Settings';
import UserProfile from './pages/UserProfile';
import AboutUs from './pages/AboutUs';
import HomePage from './pages/HomePage';
import NotificationSettings from './pages/NotificationSettings';
import AdminPanel from './pages/AdminPanel';
import Inbox from './pages/Inbox';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './components/AuthCallback';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import { BackgroundAudioProvider } from './contexts/BackgroundAudioContext';
import AudioToggle from './components/AudioToggle';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('mojo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Component to check for session_id in URL hash (for Google OAuth callback)
function AppRouter({ user, setUser, loading, handleAuth, handleLogout }) {
  const location = useLocation();
  const [introSplashDone, setIntroSplashDone] = useState(() => {
    return sessionStorage.getItem('mojo_intro_shown') === 'true';
  });
  
  // PRIORITY 1: Google OAuth callback
  if (window.location.hash?.includes('session_id=') || location.hash?.includes('session_id=')) {
    return <AuthCallback onAuth={handleAuth} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-2xl font-bold" style={{ fontFamily: 'Fredoka' }}>Loading MyMojo...</div>
      </div>
    );
  }

  // PRIORITY 2: Intro splash → About Us (public landing)
  if (!introSplashDone && !user) {
    return <SplashScreen onComplete={() => {
      sessionStorage.setItem('mojo_intro_shown', 'true');
      setIntroSplashDone(true);
    }} />;
  }

  return (
    <Routes>
      {/* Non-logged-in: About Us is the landing page */}
      <Route path="/" element={user ? <Navigate to="/splash" /> : <AboutUs showAuthButton={true} />} />
      <Route path="/login" element={user ? <Navigate to="/splash" /> : <Landing onAuth={handleAuth} />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/about" element={<AboutUs showAuthButton={!user} />} />
      <Route path="/splash" element={user ? <SplashScreen /> : <Navigate to="/" />} />
      <Route path="/dashboard" element={user ? <HomePage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/check-in" element={user ? <DashboardV2 user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/profile" element={user ? <UserProfile user={user} /> : <Navigate to="/" />} />
      <Route path="/onboarding" element={user ? <Onboarding /> : <Navigate to="/" />} />
      <Route path="/settings" element={user ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/history" element={user ? <History user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/timeline" element={user ? <MoodTimeline user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/chat" element={user ? <AIChat user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/voice-chat" element={user ? <VoiceChatPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/community" element={user ? <CommunityEnhanced user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/journal" element={user ? <Journal user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/music" element={user ? <MusicEnhanced user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/notifications" element={user ? <NotificationSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/admin" element={user ? <AdminPanel /> : <Navigate to="/" />} />
      <Route path="/inbox" element={user ? <Inbox user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('mojo_token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('mojo_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Request browser notification permission when user logs in
  useEffect(() => {
    if (user && 'Notification' in window && Notification.permission === 'default') {
      // Small delay to not overwhelm user immediately
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Auto-logout after 5 minutes of inactivity (except when music playing)
  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      const inactive = Date.now() - lastActivity;
      const fiveMinutes = 5 * 60 * 1000;
      
      // Don't logout if music is playing
      if (inactive > fiveMinutes && !isMusicPlaying) {
        handleLogout();
        alert('Session expired due to inactivity. Please login again.');
      }
    };

    const interval = setInterval(checkInactivity, 30000); // Check every 30 seconds

    // Track user activity
    const updateActivity = () => setLastActivity(Date.now());
    window.addEventListener('click', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [user, lastActivity, isMusicPlaying]);

  const handleAuth = (token, userData) => {
    localStorage.setItem('mojo_token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    console.log('🔴 handleLogout called from App.js');
    localStorage.removeItem('mojo_token');
    localStorage.clear();
    setUser(null);
    // Force complete page reload to login
    window.location.href = '/';
  };

  return (
    <BrowserRouter>
      <BackgroundAudioProvider>
        <LanguageProvider user={user}>
          <Toaster position="top-right" />
          <AudioToggle />
          <AppRouter 
            user={user} 
            setUser={setUser} 
            loading={loading} 
            handleAuth={handleAuth} 
            handleLogout={handleLogout} 
          />
        </LanguageProvider>
      </BackgroundAudioProvider>
    </BrowserRouter>
  );
}

export default App;